import SalesOfficeAttributes from '@albatrosdeveloper/ave-models-package/lib/schemas/configurations/sales-office/sales-office.entity';

import { MarkerGroup } from 'app/common/components/geo-manager/geo-manager.interfaces';

import { resolveStart } from './route-optimization.mapper';

/**
 * Origen del recorrido: el punto desde el que el motor sale — y al que vuelve.
 *
 * `POST /plan-multiday` exige un `warehouse`, y el motor arma cada vehículo con
 * su `start` y su `end` en ese mismo punto. O sea que el origen no es un adorno
 * del mapa: gobierna el orden de las visitas, las ETAs y el kilometraje del día,
 * incluido el tramo de vuelta.
 *
 * Por qué existe como módulo aparte: la decisión "hay origen / no hay origen" y
 * su presentación son reglas de negocio, y el drawer que las consume es un
 * componente sin spec propio. Acá son funciones puras y probadas; el componente
 * queda como glue, igual que con `visit-scope` o `manual-pin`.
 *
 * La cascada que resuelve el punto (geo de la oficina → tabla por código →
 * nada) vive en `resolveStart` y se reutiliza tal cual: duplicarla haría que dos
 * copias diverjan a la primera corrección.
 *
 * Módulo puro: entra una sales-office, sale el modelo de vista. Sin HTTP, sin
 * estado de componente, sin Angular.
 */

/** Color del marcador del origen: contrasta con la paleta de las paradas. */
export const ORIGIN_COLOR = '#111827'; // gray-900

/** Nombre del grupo de marcadores del origen en el mapa. */
export const ORIGIN_GROUP_NAME = 'Origen del recorrido';

/** Título del marcador cuando la oficina no declara nombre. */
export const ORIGIN_FALLBACK_TITLE = 'Oficina de venta (origen)';

/**
 * Marcador del origen para el mapa, o `undefined` si la oficina no tiene un
 * punto resoluble.
 *
 * El marcador va **sin** `order`: el `GeoManager` numera los pines que lo traen,
 * y el origen no es una parada de la secuencia. Esa ausencia es lo que lo hace
 * distinguible a simple vista.
 */
export function originMarkerGroup(salesOffice?: SalesOfficeAttributes): MarkerGroup | undefined {
  const origin = resolveStart(salesOffice);
  if (!origin) return undefined;

  return {
    groupName: ORIGIN_GROUP_NAME,
    groupColor: ORIGIN_COLOR,
    markers: [
      {
        title: salesOffice?.name || ORIGIN_FALLBACK_TITLE,
        subtitle: 'Salida y regreso del recorrido',
        latitude: origin.lat,
        longitude: origin.lon,
      },
    ],
  };
}

/**
 * ¿Se puede planificar la semana de esta sub-ruta?
 *
 * Sin origen no se planifica. La alternativa —anclar el plan en la primera
 * parada del alcance, que es el orden en que llegaron los clientes— produce
 * tiempos y distancias que no corresponden a ninguna operación real y que el
 * usuario no tiene forma de detectar. Es el mismo criterio que "Rutas AI" aplica
 * al planificar por oficina.
 */
export function canPlanMultiday(salesOffice?: SalesOfficeAttributes): boolean {
  return !!resolveStart(salesOffice);
}

/**
 * Aviso de por qué no se pudo planificar, **nombrando la oficina**.
 *
 * El nombre no es cortesía: es lo que convierte un fallo opaco en una tarea con
 * dueño, porque señala exactamente qué registro del maestro hay que geocodificar.
 */
export function missingOriginMessage(salesOffice?: SalesOfficeAttributes): string {
  const name = salesOffice?.name || 'la oficina de la sub-ruta';
  return `Sin geolocalización de oficina (${name}): no se puede planificar la semana. Cargá su ubicación en el maestro de oficinas de venta.`;
}
